import React, { useState } from "react";
import { AppShell } from "../components/duvi/AppShell";
import { Card } from "../components/duvi/Card";
import { Button } from "../components/duvi/Button";
import { Badge } from "../components/duvi/Badge";
import { Input } from "../components/duvi/Input";
import { Select } from "../components/duvi/Select";
import { Plus, Search, BookOpen, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router";
import { kb, type KBArticle } from "../api/client";
import { useQuery } from "../hooks/useApi";
import { testIds } from "@duvi-agency/ui";

export function KnowledgeBaseList() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteArticleId, setDeleteArticleId] = useState<string | null>(null);

  const {
    data: articles,
    isLoading,
    refetch,
  } = useQuery(
    () =>
      kb.list({
        category: selectedCategory || undefined,
        search: searchQuery || undefined,
      }),
    [selectedCategory, searchQuery],
  );

  const categories = ["All", "Technical", "Business", "Support"];
  const allTags = [
    "Security",
    "MFA",
    "Setup",
    "Pricing",
    "Strategy",
    "Revenue",
    "AWS",
    "Infrastructure",
    "DevOps",
    "Onboarding",
    "Support",
    "Customers",
    "Customization",
    "UX",
    "Configuration",
    "Integration",
    "API",
    "Development",
  ];

  const handleDeleteArticle = (id: string) => {
    setDeleteArticleId(id);
  };

  const confirmDelete = async () => {
    if (deleteArticleId) {
      await kb.delete(deleteArticleId);
      refetch();
    }
    setDeleteArticleId(null);
  };

  const EmptyState = () => (
    <div
      data-testid={testIds.kbEmpty}
      className="flex flex-col items-center justify-center py-16"
    >
      <BookOpen
        className="w-16 h-16 text-duvi-text-secondary mb-6"
        strokeWidth={1}
      />
      <h2
        className="text-duvi-text-primary mb-2"
        style={{
          fontSize: "var(--text-lg)",
          fontWeight: "var(--font-weight-semibold)",
        }}
      >
        No articles yet
      </h2>
      <p
        className="text-duvi-text-secondary mb-6"
        style={{ fontSize: "var(--text-sm)" }}
      >
        Create your first knowledge base article.
      </p>
      <Button variant="primary" onClick={() => navigate("/knowledge-base/new")}>
        <Plus className="w-4 h-4 mr-2" />
        New article
      </Button>
    </div>
  );

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Technical":
        return "default";
      case "Business":
        return "success";
      case "Support":
        return "warning";
      default:
        return "secondary";
    }
  };

  return (
    <AppShell
      breadcrumbs={["Knowledge Base"]}
      activeMenuItem="Knowledge Base"
      notificationCount={3}
    >
      <div data-testid={testIds.kbListPage} className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1
              className="text-duvi-text-primary"
              style={{
                fontSize: "var(--text-xl)",
                fontWeight: "var(--font-weight-semibold)",
                lineHeight: "var(--line-height-heading)",
              }}
            >
              Knowledge Base
            </h1>
          </div>
          <Button
            variant="primary"
            onClick={() => navigate("/knowledge-base/new")}
          >
            <Plus className="w-4 h-4 mr-2" />
            New article
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          {/* Category Dropdown */}
          <Select
            data-testid={testIds.kbFilterCategory}
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories
              .filter((c) => c !== "All")
              .map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
          </Select>

          {/* Tags Multiselect */}
          <div className="relative flex-1 max-w-xs">
            <div className="px-3 py-2 bg-duvi-surface border border-duvi-border rounded-[6px] min-h-[40px] flex flex-wrap gap-2 items-center">
              {selectedTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-duvi-elevated rounded text-duvi-text-primary"
                  style={{ fontSize: "var(--text-xs)" }}
                >
                  {tag}
                  <button
                    onClick={() =>
                      setSelectedTags(selectedTags.filter((t) => t !== tag))
                    }
                    className="hover:text-duvi-destructive"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                placeholder={
                  selectedTags.length === 0 ? "Filter by tags..." : ""
                }
                className="flex-1 min-w-[100px] bg-transparent outline-none text-duvi-text-primary"
                style={{ fontSize: "var(--text-sm)" }}
              />
            </div>
          </div>

          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-duvi-text-secondary" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search articles..."
              className="pl-10"
            />
          </div>
        </div>

        {/* Articles Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <span
              className="text-duvi-text-secondary"
              style={{ fontSize: "var(--text-sm)" }}
            >
              Loading articles...
            </span>
          </div>
        ) : !articles || articles.length === 0 ? (
          <Card className="p-6">
            <EmptyState />
          </Card>
        ) : (
          <div className="grid grid-cols-3 gap-6">
            {articles.map((article) => (
              <Card
                key={article.article_id}
                data-testid={testIds.kbRow}
                className="p-5 cursor-pointer hover:border-duvi-accent transition-colors group relative"
                onClick={() =>
                  navigate(`/knowledge-base/${article.article_id}`)
                }
              >
                {/* Delete Icon (appears on hover) */}
                <button
                  data-testid={testIds.kbActionDelete}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteArticle(article.article_id);
                  }}
                  className="absolute top-4 right-4 p-1.5 bg-duvi-surface border border-duvi-border rounded hover:bg-duvi-destructive hover:border-duvi-destructive hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="w-4 h-4" />
                </button>

                {/* Category Badge */}
                <div className="mb-3">
                  <Badge variant={getCategoryColor(article.category)}>
                    {article.category}
                  </Badge>
                </div>

                {/* Title */}
                <h3
                  className="text-duvi-text-primary mb-2 line-clamp-2"
                  style={{
                    fontSize: "var(--text-lg)",
                    fontWeight: "var(--font-weight-bold)",
                    lineHeight: "var(--line-height-heading)",
                  }}
                >
                  {article.title}
                </h3>

                {/* Preview */}
                <p
                  className="text-duvi-text-secondary mb-3 line-clamp-3"
                  style={{ fontSize: "var(--text-xs)" }}
                >
                  {article.content}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 bg-duvi-elevated rounded text-duvi-text-secondary"
                      style={{ fontSize: "var(--text-xs)" }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Updated At */}
                <div className="flex items-center gap-2 pt-3 border-t border-duvi-border">
                  <span
                    className="text-duvi-text-secondary"
                    style={{ fontSize: "var(--text-2xs)" }}
                  >
                    Updated {article.updated_at}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteArticleId !== null && (
          <div
            data-testid={testIds.kbDeleteDialog}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          >
            <Card className="p-6 max-w-md w-full mx-4">
              <h3
                className="text-duvi-text-primary mb-2"
                style={{
                  fontSize: "var(--text-lg)",
                  fontWeight: "var(--font-weight-semibold)",
                }}
              >
                Delete this article?
              </h3>
              <p
                className="text-duvi-text-secondary mb-6"
                style={{ fontSize: "var(--text-sm)" }}
              >
                This action cannot be undone. The article will be permanently
                removed from the knowledge base.
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  data-testid={testIds.kbDeleteDialogCancel}
                  onClick={() => setDeleteArticleId(null)}
                  className="text-duvi-text-secondary hover:text-duvi-text-primary transition-colors"
                  style={{ fontSize: "var(--text-sm)" }}
                >
                  Cancel
                </button>
                <Button
                  data-testid={testIds.kbDeleteDialogConfirm}
                  variant="destructive"
                  onClick={confirmDelete}
                >
                  Delete
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </AppShell>
  );
}
